import { Component, Input, Output, EventEmitter } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/services/user/auth.service';
import { GoogleAuthComponent } from '../auth/google-auth.component';
import { UserProfileImageComponent } from '../user-profile-image/user-profile-image.component';
import { AUTH_MESSAGES } from '../../constants/auth-messages.constants';

@Component({
  selector: 'app-mobile-header-hamburger',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    GoogleAuthComponent,
    UserProfileImageComponent,
  ],
  template: `
    <div
      *ngIf="showMenu"
      class="fixed inset-0 z-50 bg-white dark:bg-gray-900 text-gray-900 dark:text-white overflow-auto"
    >
      <div class="flex justify-end p-4">
        <button
          (click)="close.emit()"
          aria-label="Close"
          class="text-2xl font-bold p-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="px-6 pb-10">
        <!-- Logged In State -->
        <ng-container
          *ngIf="auth.currentUser$ | async as user; else loggedOutMenu"
        >
          <div
            class="flex items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-4"
          >
            <app-user-profile-image
              [displayName]="user.displayName"
              [email]="user.email"
              [photoURL]="user.photoURL"
              [size]="48"
              class="mr-4"
            >
            </app-user-profile-image>
            <div>
              <p class="text-lg font-semibold">
                {{ user.displayName || 'User' }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400 truncate">
                {{ user.email }}
              </p>
            </div>
          </div>

          <a
            [routerLink]="['/profile']"
            (click)="close.emit()"
            class="block py-3 text-base font-medium hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-3"
          >
            Profile
          </a>
          <div
            class="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6"
          ></div>
        </ng-container>

        <!-- Logged Out State -->
        <ng-template #loggedOutMenu>
          <div class="text-xl font-bold mb-4">
            {{ authMessages.signInTitle }}
          </div>
          <p
            class="text-sm font-sans font-normal text-gray-600 dark:text-gray-300 mb-4"
          >
            {{ authMessages.signInSubtitle }}
          </p>

          <ul
            class="text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-2 text-left"
          >
            <li
              *ngFor="let feature of authMessages.features"
              class="flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span>{{ feature.text }}</span>
            </li>
          </ul>

          <app-google-auth
            (googleSignIn)="googleSignInClicked()"
          ></app-google-auth>

          <div
            class="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6"
          ></div>
        </ng-template>

        <!-- Common Links (visible whether logged in or out) -->
        <div class="">
          <a
            [routerLink]="['/about']"
            (click)="close.emit()"
            class="block py-2 text-sm"
            >About</a
          >
          <a
            [routerLink]="['/terms-of-service']"
            (click)="close.emit()"
            class="block py-2 text-sm"
            >Terms of Service</a
          >
          <a
            [routerLink]="['/privacy-policy']"
            (click)="close.emit()"
            class="block py-2 text-sm"
            >Privacy</a
          >
        </div>

        <!-- Sign Out Button (Only shown when logged in) -->
        <div
          *ngIf="auth.currentUser$ | async"
          class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6"
        >
          <button
            (click)="signOut()"
            class="w-full flex items-center justify-center py-3 text-sm font-medium text-red-600 dark:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [],
})
export class MobileHeaderHamburgerComponent {
  @Input() showMenu = false;
  @Output() close = new EventEmitter<void>();
  @Output() continueWithGoogle = new EventEmitter<void>();
  @Output() continueWithApple = new EventEmitter<void>();
  authMessages = AUTH_MESSAGES;

  constructor(public auth: AuthService) {}

  googleSignInClicked(): void {
    this.continueWithGoogle.emit();
    this.close.emit();
  }

  // TODO: Add Apple sign-in when available
  appleSignInClicked(): void {
    this.continueWithApple.emit();
    this.close.emit();
  }

  signOut(): void {
    this.auth.signOut();
    this.close.emit();
  }
}
